import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ask,
  connectSource,
  getDocumentStatus,
  getGraphEntities,
  getGraphRelationships,
  getGraphStats,
  listSources,
  search,
  uploadFiles,
  type AskRequestPayload,
  type AskResponse,
  type ConnectorRequest,
  type ConnectorResponse,
  type DocumentIngestionStatus,
  type GraphEntitiesResponse,
  type GraphRelationshipsResponse,
  type GraphStatsResponse,
  type SearchResponse,
  type UploadResponse,
} from '../services/knowledge';

export const knowledgeKeys = {
  root: ['knowledge'] as const,
  sources: () => [...knowledgeKeys.root, 'sources'] as const,
  search: (query: string) => [...knowledgeKeys.root, 'search', query] as const,
  graphStats: (workspaceId?: string) =>
    [...knowledgeKeys.root, 'graph-stats', workspaceId ?? 'default'] as const,
  graphEntities: (workspaceId?: string, limit = 300) =>
    [...knowledgeKeys.root, 'graph-entities', workspaceId ?? 'default', limit] as const,
  graphRelationships: (workspaceId?: string, limit = 400) =>
    [...knowledgeKeys.root, 'graph-relationships', workspaceId ?? 'default', limit] as const,
  documentStatus: (documentId: string) =>
    [...knowledgeKeys.root, 'document-status', documentId] as const,
};

export const useKnowledgeSources = () => {
  return useQuery(knowledgeKeys.sources(), listSources, {
    staleTime: 30_000,
  });
};

export const useUploadFiles = () => {
  const client = useQueryClient();
  return useMutation<UploadResponse, Error, File[]>(uploadFiles, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: knowledgeKeys.sources() });
    },
  });
};

export const useConnectSource = () => {
  const client = useQueryClient();
  return useMutation<ConnectorResponse, Error, { provider: string; payload: ConnectorRequest }>(
    ({ provider, payload }) => connectSource(provider, payload),
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: knowledgeKeys.sources() });
      },
    },
  );
};

export const useKnowledgeSearch = () => {
  return useMutation<SearchResponse, Error, string>((query) => search(query));
};

export const useAsk = () => {
  return useMutation<AskResponse, Error, AskRequestPayload>((payload) => ask(payload));
};

export const useGraphStats = (workspaceId?: string) => {
  return useQuery<GraphStatsResponse, Error>(
    knowledgeKeys.graphStats(workspaceId),
    () => getGraphStats(workspaceId),
    {
      staleTime: 30_000,
    },
  );
};

export const useDocumentStatus = (documentId: string | null | undefined) => {
  return useQuery<DocumentIngestionStatus, Error>(
    knowledgeKeys.documentStatus(documentId ?? ''),
    () => getDocumentStatus(documentId ?? ''),
    {
      enabled: Boolean(documentId),
      refetchInterval: (data) => {
        const status = String(data?.status ?? '').toUpperCase();
        return status === 'INDEXING' || status === 'UPLOADED' || status === 'PROCESSING'
          ? 3000
          : false;
      },
    },
  );
};

export const useGraphEntities = (workspaceId?: string, limit = 300) => {
  return useQuery<GraphEntitiesResponse, Error>(
    knowledgeKeys.graphEntities(workspaceId, limit),
    () => getGraphEntities(workspaceId, limit),
    {
      staleTime: 30_000,
    },
  );
};

export const useGraphRelationships = (workspaceId?: string, limit = 400) => {
  return useQuery<GraphRelationshipsResponse, Error>(
    knowledgeKeys.graphRelationships(workspaceId, limit),
    () => getGraphRelationships(workspaceId, limit),
    {
      staleTime: 30_000,
    },
  );
};
